import React, { useState, useEffect, useMemo } from "react";
import { getAllStudents } from "../../services/studentsService";
import { computeStudentFinance, matchLevelConfig, getPaymentModeConfig, computeTotalDue } from "../../utils/feeCalculator";
import { getPayments as getAllPayments, addPayment as addPaymentTx } from "../../services/transactionsService";
import { getCurrentUser } from "../../auth";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { db } from "../../services/firebase";
import { collection, getDocs, addDoc } from "firebase/firestore";

export default function Transactions() {
  const [payments, setPayments] = useState([]);
  const currentUser = getCurrentUser();
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";

  useEffect(() => {
    (async () => {
      if (USE_LOCAL_FALLBACK) {
        const txs = await getAllPayments();
        setPayments(txs);
      } else {
        try {
          const snap = await getDocs(collection(db, "payments"));
          const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setPayments(rows);
        } catch {
          const txs = await getAllPayments();
          setPayments(txs);
        }
      }
    })();
  }, []);

  // Optional: reload payments if localStorage changes elsewhere
  useEffect(() => {
    const handler = () => setPayments(JSON.parse(localStorage.getItem("payments")) || []);
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const [filters, setFilters] = useState({
    start: "", end: "", studentId: "", campus: isAdmin ? "" : userCampus, method: "", min: "", max: ""
  });

  const filtered = useMemo(() => {
    const currentUser = getCurrentUser();
    const userCampus = String(currentUser?.campus || "").toLowerCase();
    const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
    const gated = isAdmin ? payments : payments.filter(pp => {
      const pc = String(pp.campus || "").toLowerCase();
      if (pc) return pc === userCampus;
      const st = getAllStudents().find(s => String(s.id) === String(pp.studentId));
      const sc = String(st?.campus || "").toLowerCase();
      return sc === userCampus;
    });
    return gated.filter(p => {
      const d = new Date(p.date);
      if (filters.start && d < new Date(filters.start)) return false;
      if (filters.end && d > new Date(filters.end)) return false;
      if (filters.studentId && p.studentId !== filters.studentId) return false;
      if (filters.campus && String((p.campus || "")).toLowerCase() !== String(filters.campus).toLowerCase()) return false;
      if (filters.method && p.method !== filters.method) return false;
      if (filters.min && p.amount < Number(filters.min)) return false;
      if (filters.max && p.amount > Number(filters.max)) return false;
      return true;
    });
  }, [payments, filters]);

  const total = useMemo(() => filtered.reduce((sum, p) => sum + Number(p.amount || 0), 0), [filtered]);
  const outstandingTotal = useMemo(() => {
    const currentUser = getCurrentUser();
    const userCampus = currentUser?.campus;
    const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
    const students = getAllStudents();
    let scopedStudents = isAdmin ? students : students.filter(s => (s.campus || "") === userCampus);
    if (filters.campus) scopedStudents = scopedStudents.filter(s => (s.campus || "") === filters.campus);
    if (filters.studentId) scopedStudents = scopedStudents.filter(s => String(s.id) === String(filters.studentId));
    return scopedStudents.reduce((sum, s) => {
      let studentPays = payments.filter(p => String(p.studentId) === String(s.id));
      if (filters.start) studentPays = studentPays.filter(p => new Date(p.date) >= new Date(filters.start));
      if (filters.end) studentPays = studentPays.filter(p => new Date(p.date) <= new Date(filters.end));
      const mode = s.paymentMode || "Annual";
      // Recompute from tuitionConfigs (source of truth)
      const finCfg = computeStudentFinance({ ...s, paymentMode: mode }, studentPays, "Annual");
      const recomputedTotal = Number(finCfg.totalDue || computeTotalDue(getPaymentModeConfig(matchLevelConfig(s.campus, s.gradeLevel), mode)) || 0);
      // Use saved only if it matches recomputed total
      const savedTotal = Number(s.paymentSchedule?.totalDue || 0);
      const totalDue = (s.paymentSchedule?.totalDue && Math.abs(savedTotal - recomputedTotal) < 0.5)
        ? savedTotal
        : recomputedTotal;
      const paid = studentPays.reduce((t, p) => t + Number(p.amount || 0), 0);
      const outstanding = Math.max(0, totalDue - paid);
      return sum + outstanding;
    }, 0);
  }, [payments, filters]);

  const exportCSV = () => {
    const header = "Date,Student ID,Student Name,Campus,Method,Reference,Amount\n";
    const allStudents = getAllStudents();
    const rows = filtered.map(p => {
      const student = allStudents.find(s => String(s.id) === String(p.studentId));
      return `${p.date},${p.studentId},${student ? (student.fullname || student.name) : "Unknown"},${p.campus || ""},${p.method},${p.reference},${p.amount}`;
    }).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transactions.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Modal state and handlers (must be inside component)
  const [showModal, setShowModal] = useState(false);
  const accounts = (() => {
    try {
      const raw = localStorage.getItem("accounts");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();
  const incomeAccounts = accounts.filter(a => a.type === "Income");
  const [form, setForm] = useState({ date: "", studentId: "", campus: "", method: "", reference: "", amount: "", accountId: "" });

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setForm({ date: "", studentId: "", campus: "", method: "", reference: "", amount: "", accountId: "" });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const currentUser = getCurrentUser();
    const userCampus = String(currentUser?.campus || "").toLowerCase();
    const newTx = { ...form, campus: (form.campus || userCampus || "").toLowerCase(), amount: Number(form.amount) };
    if (USE_LOCAL_FALLBACK) {
      const updated = addPaymentTx(newTx);
      setPayments(updated);
    } else {
      try {
        await addDoc(collection(db, "payments"), newTx);
        const snap = await getDocs(collection(db, "payments"));
        setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {
        const updated = addPaymentTx(newTx);
        setPayments(updated);
      }
    }
    // Do not auto-write to collections here; Daily Cash Flow is the sole collections entry point.
    handleCloseModal();
  };

  return (
    <div className="container mt-4">
      <h2>Collections & Transactions</h2>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3" style={{background:"#fffde7"}}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Total Collection</span>
              <i className="fas fa-coins text-warning"></i>
            </div>
            <div className="display-6 fw-bold text-success">₱{total.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3" style={{background:"#fffde7"}}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Transactions</span>
              <i className="fas fa-receipt text-primary"></i>
            </div>
            <div className="display-6 fw-bold">{filtered.length}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3" style={{background:"#fffde7"}}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Outstanding Balance</span>
              <i className="fas fa-exclamation-circle text-danger"></i>
            </div>
            <div className="display-6 fw-bold text-danger">₱{Number(outstandingTotal || 0).toLocaleString()}</div>
          </div>
        </div>
      </div>
      {/* Advanced Filters */}
      <div className="card p-3 mb-3">
        <h5 className="mb-3">Filter Transactions</h5>
        <div className="row g-2">
          <div className="col-md-2">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={filters.start} onChange={e => setFilters(f => ({ ...f, start: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={filters.end} onChange={e => setFilters(f => ({ ...f, end: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Student ID</label>
            <input className="form-control" placeholder="Student ID" value={filters.studentId} onChange={e => setFilters(f => ({ ...f, studentId: e.target.value }))} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Campus</label>
            <select
              className="form-select"
              value={filters.campus}
              onChange={e => setFilters(f => ({ ...f, campus: e.target.value }))}
              disabled={!isAdmin}
            >
              {isAdmin && <option value="">All</option>}
              <option value="gapan">gapan</option>
              <option value="bulacan">bulacan</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Payment Method</label>
            <select className="form-select" value={filters.method} onChange={e => setFilters(f => ({ ...f, method: e.target.value }))}>
              <option value="">All Methods</option>
              <option>Cash</option>
              <option>Bank Transfer</option>
              <option>GCash</option>
            </select>
          </div>
          <div className="col-md-1">
            <label className="form-label">Min Amount</label>
            <input type="number" className="form-control" placeholder="Min" value={filters.min} onChange={e => setFilters(f => ({ ...f, min: e.target.value }))} />
          </div>
          <div className="col-md-1">
            <label className="form-label">Max Amount</label>
            <input type="number" className="form-control" placeholder="Max" value={filters.max} onChange={e => setFilters(f => ({ ...f, max: e.target.value }))} />
          </div>
        </div>
        <div className="mt-3 d-flex gap-2">
          <button className="btn btn-secondary" onClick={() => setFilters({ start: "", end: "", studentId: "", campus: "", method: "", min: "", max: "" })}>
            Reset Filters
          </button>
          <button className="btn btn-outline-primary" onClick={exportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Transactions</h5>
          <button className="btn btn-warning" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus me-2"></i>Add Transaction
          </button>
        </div>
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Student ID</th>
              <th>Student Name</th>
              <th>Campus</th>
              <th>Description</th>
              <th>Method</th>
              <th>OR Number</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((tx, idx) => {
              const student = getAllStudents().find(s => String(s.id) === String(tx.studentId));
              return (
                <tr key={idx}>
                  <td>{tx.date}</td>
                  <td>{tx.studentId}</td>
                  <td>{student ? (student.fullname || student.name) : "Unknown"}</td>
                  <td>{tx.campus || student?.campus || ""}</td>
                  <td>{tx.description || ""}</td>
                  <td>{tx.method}</td>
                  <td>{tx.reference}</td>
                  <td className="text-end">₱{Number(tx.amount).toLocaleString()}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="text-center text-muted">No transactions found</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan="6" className="text-end">Total</th>
              <th className="text-end">₱{total.toLocaleString()}</th>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Modal for Add/Edit Transaction */}
      {showModal && (
        <div className="modal show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Add Transaction</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control" name="date" value={form.date} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Account</label>
                  <select className="form-select" name="accountId" value={form.accountId} onChange={handleChange} required>
                    <option value="">Select Income Account</option>
                    {incomeAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Student ID</label>
                  <input className="form-control" name="studentId" value={form.studentId} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Campus</label>
                  <input className="form-control" name="campus" value={form.campus} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Method</label>
                  <select className="form-select" name="method" value={form.method} onChange={handleChange} required>
                    <option value="">Select Method</option>
                    <option>Cash</option>
                    <option>Bank Transfer</option>
                    <option>GCash</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Reference</label>
                  <input className="form-control" name="reference" value={form.reference} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input type="number" className="form-control" name="amount" value={form.amount} onChange={handleChange} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
